<template>
    <div @mouseover="isHovered = true"
         @mouseleave="isHovered = false"
         class="code-editor-container">
        <codemirror v-model="val"
                    @input="onInput"
                    @blur="onBlur"
                    @ready="onReady"
                    @focus="onFocus"
                    :id="`json-editor-${id}`"
                    :ref="`json-editor-${id}`"
                    :options="cmOptions"
                    class="h-100 w-100">
        </codemirror>

        <div class="editor-header">
            <slot name="editor-header" :isValid="isValid"></slot>
        </div>

        <div v-if="isHovered && !readOnly" class="editor-footer">
            <button v-if="!isValid"
                    v-tooltip="'Invalid code'"
                    @click="formatCode"
                    class="btn btn-sm btn-outline-danger footer-btn ml-2">
                <i class="fa fa-info-circle"></i>
            </button>
            <button v-tooltip="'Format code'"
                    @click="formatCode"
                    class="btn btn-sm btn-primary footer-btn ml-2">
                <i class="fa fa-indent"></i>
            </button>

            <slot name="bottom-right-actions"></slot>
        </div>
    </div>
</template>

<script>
import {JSHINT} from 'jshint'
import beautify from 'js-beautify'
import {codemirror} from 'vue-codemirror'
import CodeMirror from 'codemirror/lib/codemirror'
import xmlParser from 'fast-xml-parser'
import 'codemirror/addon/display/placeholder'
import 'codemirror/lib/codemirror.css'
import 'codemirror/theme/monokai.css'
import 'codemirror/mode/javascript/javascript.js'
import 'codemirror/addon/lint/lint.css'
import 'codemirror/addon/lint/lint'
import 'codemirror/addon/lint/javascript-lint'
import 'codemirror/addon/edit/matchbrackets'
import 'codemirror/addon/edit/closebrackets'
import 'codemirror/addon/hint/javascript-hint.js'
import 'codemirror/addon/hint/show-hint.js'
import 'codemirror/addon/hint/show-hint.css'
import 'codemirror/addon/hint/anyword-hint'
import {getRandomId} from '@/utils'

window.JSHINT = JSHINT

export default {
    name: 'AppCodeEditor',
    components: {codemirror},
    props: {
        id: {
            type: String,
            required: false,
            default: getRandomId
        },
        value: {
            required: true
        },
        mimeType: {
            type: String,
            default: 'application/json',
            validator: val => [
                'application/json',
                'text/javascript',
                'application/ld+json',
                'text/x-mathematica',
                'application/xml'
            ].indexOf(val) !== -1
        },
        readOnly: {
            type: Boolean,
            default: false
        },
        lint: {
            type: Boolean,
            default: true
        },
        placeholder: {
            type: String,
            default: ''
        },
        theme: {
            type: String,
            default: 'monokai',
            validator: val => [
                'monokai',
                'default'
            ].indexOf(val) !== -1
        },
        codemirrorOptions: {
            type: Object,
            default: () => ({})
        },
        autocompleteOnAnyKeyPress: {
            type: Boolean,
            required: false,
            default: true
        },
        withLanguageHints: {
            type: Boolean,
            required: false,
            default: false
        },
        withAnyWordHints: {
            type: Boolean,
            required: false,
            default: false
        },
        customHints: {
            type: Array,
            default: () => {
                // Array<Hint>
                // @see addon/hint/show-hint => Hint
                return [
                    // {
                    // text: 'test'
                    // displayText?: string | undefined;
                    // }
                ]
            },
            required: false
        }
    },
    data() {
        return {
            val: '',
            isHovered: false,

            cmOptions: {
                mode: this.mimeType,
                theme: this.theme,
                gutters: ['CodeMirror-lint-markers'],
                matchBrackets: true,
                autoCloseBrackets: true,
                styleActiveLine: true,
                lineWrapping: true,
                lineNumbers: true,
                tabSize: 2,
                line: true,
                lint: this.lint,
                extraKeys: {
                    'Ctrl-Space': 'autocomplete'
                },
                readOnly: this.readOnly,
                placeholder: this.placeholder,
                ...this.codemirrorOptions
                // extraKeys: { 'Shift-Tab': this.formatCode }
            }
        }
    },
    computed: {
        isJSON() {
            return this.mimeType === 'application/json'
        },

        isXML() {
            return this.mimeType === 'application/xml'
        },

        isJavascript() {
            return this.mimeType === 'text/javascript'
        },

        editor() {
            return this.$refs[`json-editor-${this.id}`].codemirror
        },

        isValid() {
            if (this.isJSON) {
                try {
                    return (JSON.parse(this.val) && !!this.val)
                } catch (e) {
                    return false
                }
            } else if (this.isXML) {
                try {
                    return (xmlParser.parse(this.val, {}, true) && !!this.val)
                } catch (e) {
                    return false
                }
            }
            return true
        },

        customHintsOrdered() {
            return _.cloneDeep(this.customHints).sort((a, b) => {
                if (a && typeof a.text === 'string') {
                    if (b && typeof b.text === 'string') {
                        return a.text.length - b.text.length
                    }
                    return 1
                }
                return -1
            })
        }
    },
    methods: {
        onReady(cm) {
            CodeMirror.registerHelper('hint', 'appEditorHints', (cm, options) => {
                let list = []
                const cursor = cm.getCursor()
                const token = cm.getTokenAt(cursor)
                let {string: currentWord, start, end} = token
                start = (/^["']/g).test(currentWord) ? start + 1 : start
                end = (/["']$/g).test(currentWord) ? end - 1 : end

                if (this.withAnyWordHints) {
                    list = CodeMirror.hint.anyword(cm, options).list
                }
                if (this.withLanguageHints && this.isJavascript) {
                    const jsHints = CodeMirror.hint.javascript(cm, options)
                    if (jsHints && jsHints.list) {
                        list = list.concat(CodeMirror.hint.javascript(cm, options).list)
                    }
                }
                if (this.customHints.length !== 0) {
                    const filteredHits = this.customHints
                        .filter(h => {
                            return h && typeof h.displayText === 'string' &&
                                h.displayText
                                    .replace(/["']/g, '')
                                    .includes(currentWord.trim().replace(/["']/g, ''))
                        })
                    list = list.concat(filteredHits)
                }

                list = _.uniq(list)
                if (list.length > 0) {
                    return {
                        list: list,
                        from: CodeMirror.Pos(cursor.line, start),
                        to: CodeMirror.Pos(cursor.line, end)
                    }
                }
            })

            CodeMirror.commands.autocomplete = () => {
                CodeMirror.showHint(cm, CodeMirror.hint.appEditorHints, {completeSingle: false})
            }

            if (this.autocompleteOnAnyKeyPress) {
                cm.on('keypress', () => {
                    CodeMirror.showHint(cm, CodeMirror.hint.appEditorHints, {completeSingle: false})
                })
            }

            if (this.lint) {
                if (this.isJavascript) {
                    cm.setOption('lint', {
                        options: {
                            esversion: 6,
                            lastsemic: false,
                            asi: true
                        }
                    })
                }
            }
        },

        onInput(value) {
            this.$emit('input', value, this.isValid)
            this.highlightCustomHints()
        },

        onBlur() {
            this.$emit('blur')
        },

        onFocus() {
            this.$emit('focus')
        },

        formatCode() {
            if (this.isJSON || this.isJavascript) {
                this.val = beautify.js(this.val, {indent_size: 2})
            } else if (this.isXML) {
                this.val = beautify.html(this.val, {indent_size: 2})
            }
        },

        highlightCustomHints() {
            if (this.customHints.length === 0) {
                return
            }
            this.editor.doc.getAllMarks().forEach(marker => marker.clear())

            const customHintMarkOptions = {
                className: 'text-primary-light text-bold'
            }

            for (let i = 0; i <= this.editor.lineCount(); i++) {
                const tokens = this.editor.getLineTokens(i, false)
                tokens.forEach(token => {
                    const start = CodeMirror.Pos(i, token.start)
                    const end = CodeMirror.Pos(i, token.end)
                    if (this.customHintsOrdered.some(h => h && h.text === token.string)) {
                        this.editor.markText(start, end, customHintMarkOptions)
                    } else {
                        this.editor.markText(start, end, {})
                    }
                })
            }
        },

        focus() {
            this.editor.focus()
        },

        insertTextAtCursorPosition(text) {
            const doc = this.editor.getDoc()
            const cursor = doc.getCursor()
            doc.replaceRange(text, cursor)
            this.focus()
        }
    },
    created() {
        this.val = typeof this.value === 'object' ? JSON.stringify(this.value) : this.value
        this.formatCode()
        this.$nextTick(() => { // to set the initial validity flag
            this.onInput(this.val)
        })
    }
}
</script>

<style lang="scss" scoped>
.code-editor-container {
    display: block;
    position: relative;
    width: 100%;
    height: 100%;
    text-align: left;
}

.editor-header {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 10000;
}

.editor-footer {
    position: absolute;
    bottom: 16px;
    right: 16px;
    z-index: 10000;
}

.footer-btn {
    min-width: 60px;
    padding: 4px 16px;
}
</style>

<style>
.CodeMirror-lint-tooltip {
    z-index: 10000 !important;
}

.CodeMirror-hints {
    text-align: left;
    position: absolute;
    z-index: 10000;
    width: auto;
    max-width: 240px;
    height: auto;
    overflow: scroll;
}
</style>
